import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import { FaCheckCircle, FaTimesCircle, FaSpinner } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import Logo from '../../images/logo.svg';
import arabicBg from '../../images/arabic-bg-dark.svg';
import api from '../../services/api';
import './style.css';

const VerifyEmailPage = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [verifying, setVerifying] = useState(true);
    const [verified, setVerified] = useState(false);
    const [error, setError] = useState('');
    const [redirectPath, setRedirectPath] = useState(null);
    const hasVerified = useRef(false); // Prevent double execution in React Strict Mode

    useEffect(() => {
        // Prevent double execution in React 18 Strict Mode
        if (hasVerified.current) {
            console.log('VerifyEmailPage - Already verified, skipping');
            return;
        }

        const verifyEmail = async () => {
            const token = searchParams.get('token');

            if (!token) {
                setError(t('verifyEmail.invalidLink'));
                setVerifying(false);
                return;
            }

            // Mark as verified to prevent double execution
            hasVerified.current = true;

            try {
                await api.post('/auth/verify-email', { token });
                setVerified(true);
                toast.success(t('verifyEmail.verifiedSuccess'));

                // Check if there's a post-verification action in localStorage
                // (localStorage persists across tabs, unlike sessionStorage)
                const action = localStorage.getItem('pending_verification_action');
                console.log('VerifyEmailPage - Action from localStorage:', action);

                // Determine redirect path based on action
                let path = '/dashboard'; // default
                if (action === 'level-test') {
                    path = '/level-test';
                } else if (action === 'demo') {
                    path = '/demo-confirmation';
                } else if (action === 'subscribe') {
                    path = '/subscribe';
                }

                console.log('VerifyEmailPage - Will redirect to:', path);

                // Store the redirect path in state
                setRedirectPath(path);

                // Clear the action from localStorage AFTER we've determined the path
                localStorage.removeItem('pending_verification_action');

            } catch (err) {
                const errorMessage = err.response?.data?.detail?.message || t('verifyEmail.verificationFailedDefault');
                setError(errorMessage);
                toast.error(errorMessage);
            } finally {
                setVerifying(false);
            }
        };

        verifyEmail();
    }, [searchParams, t]);

    // Separate effect for navigation to avoid double-redirect issue
    useEffect(() => {
        if (verified && redirectPath) {
            console.log('VerifyEmailPage - Redirecting to:', redirectPath);
            const timer = setTimeout(() => {
                navigate(redirectPath);
            }, 2000);

            return () => clearTimeout(timer);
        }
    }, [verified, redirectPath, navigate]);

    return (
        <div className="verify-email-page">
            {/* Arabic Background Pattern */}
            <div className="verify-email-bg-pattern" style={{ backgroundImage: `url(${arabicBg})` }} />

            {/* Header */}
            <div className="verify-email-header">
                <Link to="/" className="verify-email-logo">
                    <img src={Logo} alt={t('verifyEmail.logoAlt')} />
                </Link>
            </div>

            {/* Main Content */}
            <div className="verify-email-content">
                {verifying && (
                    <div className="verify-email-card">
                        <div className="status-icon-wrapper loading">
                            <FaSpinner className="status-icon spinning" />
                        </div>
                        <h1>{t('verifyEmail.verifyingTitle')}</h1>
                        <p>{t('verifyEmail.verifyingMessage')}</p>
                    </div>
                )}

                {!verifying && verified && (
                    <div className="verify-email-card">
                        <div className="status-icon-wrapper success">
                            <FaCheckCircle className="status-icon" />
                        </div>
                        <h1>{t('verifyEmail.verifiedTitle')}</h1>
                        <p>{t('verifyEmail.verifiedMessage')}</p>
                        <p className="redirect-text">{t('verifyEmail.redirecting')}</p>
                    </div>
                )}

                {!verifying && error && (
                    <div className="verify-email-card">
                        <div className="status-icon-wrapper error">
                            <FaTimesCircle className="status-icon" />
                        </div>
                        <h1>{t('verifyEmail.verificationFailed')}</h1>
                        <p className="error-message">{error}</p>
                        <div className="verify-actions">
                            <button
                                onClick={() => navigate('/login')}
                                className="btn-primary"
                            >
                                {t('verifyEmail.goToLogin')}
                            </button>
                            <button
                                onClick={() => navigate('/')}
                                className="btn-secondary"
                            >
                                {t('verifyEmail.backToHome')}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default VerifyEmailPage;
